import {
  DRIVE_CANONICAL_STATE_ORDER,
  type DriveCanonicalState,
  type DriveEntropyVisual,
  type DriveLaneGridKey,
  entropyVisualClass,
  laneSortRank,
  resolveLaneGridKey,
} from './driveCanonicalLanes';
import { artifactMatchesAllowedScopes, artifactScopeKeys } from './driveScope';
import { normalizeScopeIdKey, scopeIdsEqual } from '../../../utils/scopeIdPlane';
import type {
  DriveArtifactItem,
  DriveDetailLevel,
  DriveHiFiModel,
  DriveOrderFlowStage,
  DriveSubdomainNode,
} from './types';

export interface DriveTypeStateCounts {
  typeKey: string;
  typeLabel: string;
  byState: Partial<Record<DriveCanonicalState, number>>;
}

export interface DriveLaneViewModel {
  id: string;
  gridKey: DriveLaneGridKey;
  name: string;
  entropy: DriveEntropyVisual;
  types: DriveTypeStateCounts[];
  hasFlow: boolean;
  flowHint?: string;
  note?: string;
}

export function driveEmptyLaneCopy(hasFlow: boolean): string {
  return hasFlow ? 'Nothing flowing' : 'Flows not yet defined';
}

export interface DriveFlowViewModel {
  domainLabel: string;
  lanes: DriveLaneViewModel[];
}

function lifeToCanonicalState(life: DriveArtifactItem['life']): DriveCanonicalState {
  switch (life) {
    case 'drafted':
      return 'defined';
    case 'active':
      return 'active';
    case 'closed':
      return 'posted';
    case 'created':
    default:
      return 'created';
  }
}

/** Map manifest `stateCounts` keys (life or canonical) onto Drive token states. */
function stateCountKeyToCanonical(key: string): DriveCanonicalState | undefined {
  switch (key) {
    case 'created':
      return 'created';
    case 'drafted':
    case 'defined':
      return 'defined';
    case 'active':
      return 'active';
    case 'closed':
    case 'posted':
      return 'posted';
    default:
      return undefined;
  }
}

function bump(counts: Partial<Record<DriveCanonicalState, number>>, state: DriveCanonicalState) {
  counts[state] = (counts[state] ?? 0) + 1;
}

function bumpBy(counts: Partial<Record<DriveCanonicalState, number>>, state: DriveCanonicalState, n: number) {
  counts[state] = (counts[state] ?? 0) + n;
}

function typeLabelFromLane(subdomain: DriveSubdomainNode, laneId: string): string {
  const lane = subdomain.grandchildren?.find((g) => scopeIdsEqual(g.id, laneId));
  return lane?.name ?? laneId;
}

function laneParentMap(subdomains: DriveSubdomainNode[]): Map<string, string> {
  const map = new Map<string, string>();
  for (const sub of subdomains) {
    const parentKey = normalizeScopeIdKey(sub.id);
    for (const lane of sub.grandchildren ?? []) {
      const laneKey = normalizeScopeIdKey(lane.id);
      if (laneKey && parentKey) map.set(laneKey, parentKey);
    }
  }
  return map;
}

function artifactsForSubdomain(
  artifacts: DriveArtifactItem[],
  sub: DriveSubdomainNode,
  laneParents: Map<string, string>
): DriveArtifactItem[] {
  const allowed = new Set<string>();
  const add = (raw: string | undefined) => {
    const key = normalizeScopeIdKey(raw);
    if (key) allowed.add(key);
  };
  add(sub.id);
  for (const g of sub.grandchildren ?? []) add(g.id);
  return artifacts.filter((art) => artifactMatchesAllowedScopes(art, allowed, laneParents));
}

function aggregateLaneTypes(
  subdomain: DriveSubdomainNode,
  artifacts: DriveArtifactItem[]
): DriveTypeStateCounts[] {
  const laneIds = subdomain.grandchildren?.map((g) => g.id) ?? [];
  if (laneIds.length > 0) {
    return laneIds.map((laneId) => {
      const byState: Partial<Record<DriveCanonicalState, number>> = {};
      for (const art of artifacts) {
        const keys = artifactScopeKeys(art);
        const inLane =
          scopeIdsEqual(art.lane, laneId) ||
          keys.some((k) => scopeIdsEqual(k, laneId)) ||
          (scopeIdsEqual(art.subdomainId, subdomain.id) &&
            keys.some((k) => scopeIdsEqual(k, laneId)));
        if (!inLane) continue;
        bump(byState, lifeToCanonicalState(art.life));
      }
      return {
        typeKey: laneId,
        typeLabel: typeLabelFromLane(subdomain, laneId),
        byState,
      };
    });
  }

  if (artifacts.length === 0) return [];

  const byState: Partial<Record<DriveCanonicalState, number>> = {};
  for (const art of artifacts) {
    bump(byState, lifeToCanonicalState(art.life));
  }
  return [
    {
      typeKey: subdomain.id,
      typeLabel: subdomain.name,
      byState,
    },
  ];
}

/**
 * When flowArtifacts do not place on a visible lane, use subdomain.stateCounts from the
 * manifest so typed/dormant lanes still show token dots (no double-count when arts match).
 */
export function typesFromSubdomainStateCounts(
  subdomain: DriveSubdomainNode
): DriveTypeStateCounts[] {
  const counts = subdomain.stateCounts;
  if (!counts) return [];
  const byState: Partial<Record<DriveCanonicalState, number>> = {};
  Object.entries(counts).forEach(([key, raw]) => {
    if (typeof raw !== 'number' || raw <= 0) return;
    const state = stateCountKeyToCanonical(key);
    if (!state) return;
    bumpBy(byState, state, raw);
  });
  if (!Object.values(byState).some((n) => (n ?? 0) > 0)) return [];
  return [
    {
      typeKey: subdomain.id,
      typeLabel: subdomain.name,
      byState,
    },
  ];
}

function totalForType(type: DriveTypeStateCounts): number {
  return Object.values(type.byState).reduce((sum, n) => sum + (n ?? 0), 0);
}

function typesHaveAnyCount(types: DriveTypeStateCounts[]): boolean {
  return types.some((t) => totalForType(t) > 0);
}

function resolveLaneTypes(
  subdomain: DriveSubdomainNode,
  artifacts: DriveArtifactItem[]
): DriveTypeStateCounts[] {
  const fromArtifacts = aggregateLaneTypes(subdomain, artifacts);
  if (typesHaveAnyCount(fromArtifacts)) return fromArtifacts;
  const fromCounts = typesFromSubdomainStateCounts(subdomain);
  return fromCounts.length ? fromCounts : fromArtifacts;
}

const BUSINESS_DOMAIN_TYPE = 'pt-business-domain';
const PRODUCT_BRIDGE_TYPE = 'pt-product-domain-bridge';

function sectionForLane(model: DriveHiFiModel, sub: DriveSubdomainNode) {
  return (model.sections || []).find(
    (row) => row.id === sub.domainSectionId || (row.laneIds || []).includes(sub.id)
  );
}

/** Business-domain and product-bridge lanes may show the company order envelope. */
export function laneReceivesCompanyOrderFlow(model: DriveHiFiModel, sub: DriveSubdomainNode): boolean {
  const mode = String(sub.laneMode || '').toLowerCase();
  if (mode === 'product_tier') return false;
  if (mode === 'product_subdomain' || mode === 'per_product') {
    return true;
  }
  const section = sectionForLane(model, sub);
  const ref = `${section?.domainTypeRef || ''} ${sub.domainSectionTypeRef || ''}`.toLowerCase();
  return ref.includes(BUSINESS_DOMAIN_TYPE) || ref.includes(PRODUCT_BRIDGE_TYPE);
}

function typesFromOrderFlow(stages: DriveOrderFlowStage[]): DriveTypeStateCounts[] {
  return stages
    .filter((stage) => stage.wip > 0 && stage.label.trim())
    .map((stage) => ({
      typeKey: `order-stage-${stage.sequence}`,
      typeLabel: stage.label.trim(),
      byState: { active: stage.wip },
    }));
}

function flowHintForLane(types: DriveTypeStateCounts[]): string | undefined {
  if (types.length < 2) return undefined;
  const totals = types.map(totalForType);
  const first = totals[0] ?? 0;
  const last = totals[totals.length - 1] ?? 0;
  if (first > 0 && last < first * 0.5) {
    return 'left → right = acquisition flow; drop-off after first stage is the constraint';
  }
  return 'left → right = process flow order';
}

export function buildDriveFlowViewModel(model: DriveHiFiModel): DriveFlowViewModel {
  const laneParents = laneParentMap(model.subdomains);
  const lanes: DriveLaneViewModel[] = model.subdomains.map((sub) => {
    const items = artifactsForSubdomain(model.artifacts, sub, laneParents);
    let types = resolveLaneTypes(sub, items);
    let note: string | undefined;
    if (!typesHaveAnyCount(types) && laneReceivesCompanyOrderFlow(model, sub)) {
      const fromEnvelope = typesFromOrderFlow(model.orderFlowStages || []);
      if (fromEnvelope.length) {
        types = fromEnvelope;
        note = 'Company order flow';
      }
    }
    const gridKey = resolveLaneGridKey(sub.name, sub.id);
    const hasFlow = ((sub.grandEdges?.length ?? 0) > 0 || Boolean(note)) && types.length > 1;
    return {
      id: sub.id,
      gridKey,
      name: sub.name,
      entropy: entropyVisualClass(sub.entropyClass),
      types,
      hasFlow,
      flowHint: hasFlow ? flowHintForLane(types) : undefined,
      ...(note ? { note } : {}),
    };
  });

  lanes.sort((a, b) => laneSortRank(a.gridKey) - laneSortRank(b.gridKey));

  return {
    domainLabel: model.domainLabel,
    lanes,
  };
}

export function typeStateTotal(type: DriveTypeStateCounts): number {
  return totalForType(type);
}

export function orderedStateEntries(
  type: DriveTypeStateCounts
): Array<{ state: DriveCanonicalState; count: number }> {
  return DRIVE_CANONICAL_STATE_ORDER.filter((s) => (type.byState[s] ?? 0) > 0).map((state) => ({
    state,
    count: type.byState[state] ?? 0,
  }));
}

export function detailLevelToMode(lod: DriveDetailLevel): 'summary' | 'token' | 'detailed' {
  if (lod === 'summary') return 'summary';
  if (lod === 'token') return 'token';
  return 'detailed';
}
